// notificationMockData.ts
import {
  getNotificationsDatatable,
  markAllAsSeen,
  markSelectedAsSeen,
  markSingleAsSeen,
  deleteSingleNotification,
  getUnreadCount,
  NotificationRow,
} from "../../services/notificationApi";

import { getAuthCookie } from "../../utils/authCookies";

export type NotificationType =
  | "property_added"
  | "approval_needed"
  | "attribute_missing"
  | "brand_verified"
  | "dynamic_cost"
  | "new_team_member"
  | "document_expiring"
  | "payment_received";

export interface NotificationItemType {
  id: string;
  title: string;
  description: string;
  time: string;
  type: NotificationType;
  isUnread: boolean;
  dateGroup: "today" | "yesterday" | "this_week";
}

// Helper to format time ago
function formatTimeAgo(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  } catch {
    return "Recently";
  }
}

// Helper to determine date group
function getDateGroup(dateString: string): "today" | "yesterday" | "this_week" {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return "this_week";
    return "this_week";
  } catch {
    return "this_week";
  }
}

// Convert API row to UI format
function mapNotificationRow(row: NotificationRow): NotificationItemType {
  // API returns _id, but some might use notification_id
  const notificationId = row._id || row.notification_id || "";
  if (!notificationId) {
    console.warn("Notification row missing _id or notification_id:", row);
  }

  return {
    id: notificationId,
    title: row.title || "Notification",
    description: row.text || "",
    time: formatTimeAgo(row.created_at),
    type: "new_team_member", // Default type, can be enhanced later
    isUnread: !row.seen,
    dateGroup: getDateGroup(row.created_at),
  };
}

// Fetch notifications from API with pagination
export interface FetchNotificationsResponse {
  items: NotificationItemType[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchNotifications(
  userId: string,
  token: string,
  options: {
    page?: number;
    limit?: number;
    seen?: "seen" | "unseen" | "all";
    forceRefresh?: boolean;
  } = {}
): Promise<FetchNotificationsResponse> {
  const {
    page = 1,
    limit = 10,
    seen = "all",
    forceRefresh = false,
  } = options;

  try {
    const response = await getNotificationsDatatable(
      userId,
      {
        page,
        limit,
        seen,
        sort_field: "created_at",
        sort_dir: -1,
      },
      token
    );

    // Validate and map notifications
    const items = response.rows
      .filter((row) => {
        // Filter out rows without _id or notification_id
        const hasId = (row._id && row._id.trim() !== "") || (row.notification_id && row.notification_id.trim() !== "");
        if (!hasId) {
          console.warn("Skipping notification without _id or notification_id:", row);
          return false;
        }
        return true;
      })
      .map(mapNotificationRow);
    
    return {
      items,
      total: response.total || 0,
      page: response.page || page,
      limit: response.limit || limit,
    };
  } catch (error) {
    console.error("Failed to fetch notifications:", error);
    return {
      items: [],
      total: 0,
      page,
      limit,
    };
  }
}

// Get unread count
export async function fetchUnreadCount(
  userId: string,
  token: string
): Promise<number> {
  try {
    const response = await getUnreadCount(userId, token);
    return response.unread_count || 0;
  } catch (error) {
    console.error("Failed to fetch unread count:", error);
    return 0;
  }
}

/* ---------------------------
   ⭐ API Calls
--------------------------- */

// MULTIPLE MARK AS READ
export async function markAllAsReadApi(ids: string[]) {
  const userId = getAuthCookie("userId");
  const token = getAuthCookie("sessionToken");

  if (!userId || !token) {
    console.warn("Missing userId or token for markAllAsReadApi");
    return { success: false };
  }

  try {
    await markAllAsSeen(userId, token);
    return { success: true };
  } catch (error) {
    console.error("Failed to mark all as read:", error);
    return { success: false };
  }
}

// SINGLE MARK AS READ
export async function markAsReadApi(id: string) {
  const userId = getAuthCookie("userId");
  const token = getAuthCookie("sessionToken");

  if (!userId || !token) {
    console.warn("Missing userId or token for markAsReadApi");
    return { success: false };
  }

  if (!id || id === "undefined" || id === "null" || id.trim() === "") {
    console.error("Invalid notification ID:", id);
    return { success: false };
  }

  try {
    await markSingleAsSeen(userId, id.trim(), token);
    return { success: true };
  } catch (error) {
    console.error("Failed to mark as read:", error);
    return { success: false };
  }
}

// DELETE
export async function deleteNotificationApi(id: string) {
  const userId = getAuthCookie("userId");
  const token = getAuthCookie("sessionToken");

  if (!userId || !token) {
    console.warn("Missing userId or token for deleteNotificationApi");
    return { success: false };
  }

  try {
    await deleteSingleNotification(userId, id, token);
    return { success: true };
  } catch (error) {
    console.error("Failed to delete notification:", error);
    return { success: false };
  }
}

/* ---------------------------
   ⭐ Mock Notifications (Fallback)
--------------------------- */

// Fallback mock data if API fails
export const notificationMockData: NotificationItemType[] = [
  {
    id: "1",
    title: "New Property Added",
    description: "Sunset Boulevard Office has been successfully added to the system.",
    time: "5 min ago",
    type: "property_added",
    isUnread: true,
    dateGroup: "today",
  },
  {
    id: "2",
    title: "Approval Needed",
    description: "Bryan requested to join your brokerage team on RealEsy. Tap to review their request.",
    time: "1 hour ago",
    type: "approval_needed",
    isUnread: true,
    dateGroup: "today",
  },
  {
    id: "3",
    title: "Attribute Missing",
    description: "Property at 123 Main St is missing required attributes.",
    time: "3 hours ago",
    type: "attribute_missing",
    isUnread: true,
    dateGroup: "today",
  },
  {
    id: "4",
    title: "Brand Verification",
    description: "Premium Realty brand has been verified and approved.",
    time: "1 day ago",
    type: "brand_verified",
    isUnread: false,
    dateGroup: "yesterday",
  },
  {
    id: "5",
    title: "Dynamic Cost Updated",
    description: "Inspection Fee cost has been updated for all active properties.",
    time: "2 days ago",
    type: "dynamic_cost",
    isUnread: false,
    dateGroup: "this_week",
  },
  {
    id: "6",
    title: "New Team Member",
    description: "Sarah Johnson has joined your brokerage team.",
    time: "3 days ago",
    type: "new_team_member",
    isUnread: false,
    dateGroup: "this_week",
  },
  {
    id: "7",
    title: "Document Expiring Soon",
    description: "Property license for Downtown Plaza expires in 7 days.",
    time: "4 days ago",
    type: "document_expiring",
    isUnread: false,
    dateGroup: "this_week",
  },
  {
    id: "8",
    title: "Payment Received",
    description: "Monthly subscription payment of $299 has been processed successfully.",
    time: "5 days ago",
    type: "payment_received",
    isUnread: false,
    dateGroup: "this_week",
  },
];
